import * as Sentry from '@sentry/remix';
import type { RunnerError } from './browserMetroRunner.client';

const DUPLICATE_WINDOW_MS = 60_000;

/**
 * Remove per-run values that would split one preview failure into many Sentry
 * issues (blob URLs, generated line numbers, ports and long numeric IDs).
 */
export function normalizePreviewErrorMessage(message: string): string {
  return message
    .replace(/blob:[^\s)]+/gi, '<blob-url>')
    .replace(/https?:\/\/(?:localhost|127\.0\.0\.1):\d+/gi, '<local-url>')
    .replace(/:\d+:\d+/g, ':<line>:<column>')
    .replace(/\b\d{6,}\b/g, '<id>')
    .replace(/\s+/g, ' ')
    .trim();
}

export function previewErrorFingerprint(error: RunnerError): string[] {
  return ['react-native-preview', error.source, normalizePreviewErrorMessage(error.message)];
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Reports actionable RN preview failures from the parent window. A reporter is
 * scoped to one runner lifecycle so generated code never receives the Sentry
 * DSN and no source file contents are attached to events.
 */
export class ReactNativePreviewReporter {
  #lastCapturedAt = new Map<string, number>();
  #sessionId = createSessionId();
  #failuresSinceSuccess = 0;

  capture(error: RunnerError, now = Date.now()): boolean {
    const fingerprint = previewErrorFingerprint(error);
    const key = fingerprint.join('\u0000');
    const lastCapturedAt = this.#lastCapturedAt.get(key);

    if (lastCapturedAt !== undefined && now - lastCapturedAt < DUPLICATE_WINDOW_MS) {
      return false;
    }

    this.#lastCapturedAt.set(key, now);
    this.#failuresSinceSuccess += 1;

    const exception = new Error(error.message);
    exception.name = 'ReactNativePreviewError';

    if (error.stack) {
      exception.stack = `${exception.name}: ${error.message}\n${error.stack}`;
    }

    Sentry.captureException(exception, {
      level: 'error',
      fingerprint,
      tags: {
        severity: 'P2',
        feature: 'react-native-preview',
        preview_error_source: error.source,
      },
      extra: {
        preview_session_id: this.#sessionId,
        resolved_stack: error.stack,
      },
    });

    return true;
  }

  markBuildSuccess(): void {
    if (this.#failuresSinceSuccess === 0) {
      return;
    }

    Sentry.addBreadcrumb({
      category: 'react-native-preview',
      level: 'info',
      message: 'Preview recovered after a successful build',
      data: {
        preview_session_id: this.#sessionId,
        reported_failures: this.#failuresSinceSuccess,
      },
    });
    this.#failuresSinceSuccess = 0;
  }

  reset(): void {
    this.#lastCapturedAt.clear();
    this.#failuresSinceSuccess = 0;
    this.#sessionId = createSessionId();
  }
}
